import { ActionRuleParams } from '../../types';
import { Island } from '../../../islands/island.class';
import { PanelItem } from '../../../panel-items/panel-item.class';
import { PanelItemGroup } from '../../../panel-item-groups/panel-item-group.class';

const KEG_SKU = 'C124KEG';
const TAP_SKU = 'C124KEG-TAP';

const applyKegTapForIsland = (island: Island): void => {
	island.panels.children.forEach((panel) => {
		// Collect the groups that contain a kegerator, and every rule-managed tap on
		// the panel. A tap belongs paired in its kegerator's group; anything else is
		// either an old orphan (from the earlier "tap gets its own group" version of
		// this rule) or a stale tap whose kegerator was swapped out.
		const kegGroups: Array<PanelItemGroup> = [];
		const ruleManagedTaps: Array<PanelItem> = [];

		panel.panelItemGroups.children.forEach((group) => {
			let hasKeg = false;
			group.items.children.forEach((panelItem) => {
				const { sku } = panelItem.item;
				if (sku === KEG_SKU) {
					hasKeg = true;
				} else if (sku === TAP_SKU && panelItem.ruleManaged) {
					ruleManagedTaps.push(panelItem as PanelItem);
				}
			});
			if (hasKeg) kegGroups.push(group as PanelItemGroup);
		});

		// Remove rule-managed taps that aren't paired with a kegerator group
		// (covers both "no kegerator on panel" and "tap lives in its own/old group").
		ruleManagedTaps.forEach((tap) => {
			const tapGroup = tap.refMap?.panelItemGroup;
			if (!tapGroup || !kegGroups.includes(tapGroup as PanelItemGroup)) {
				tapGroup?.items.delete(tap.id);
			}
		});

		// Ensure every kegerator group has a tap sibling. Once paired, the tap
		// follows the kegerator on any group-level move (PanelItemGroup.move and
		// PanelItem.moveCfn's linked-group propagation both handle that).
		kegGroups.forEach((kegGroup) => {
			const hasTap = kegGroup.items.children.some((panelItem) => panelItem.item.sku === TAP_SKU);
			if (!hasTap) {
				const tap = kegGroup.items.newFromSku(TAP_SKU);
				tap.ruleManaged = true;
			}
		});
	});
};

export const requiresKegTap = (params: ActionRuleParams): void => {
	const { refMap } = params;
	const { island } = refMap;

	// single-island scope -- apply directly
	if (island) {
		applyKegTapForIsland(island);
		return;
	}

	// kitchen scope -- iterate all islands
	const islands = refMap.islands ?? refMap.kitchen?.design?.islands ?? null;
	if (!islands) return;

	for (const child of islands.children) {
		applyKegTapForIsland(child as Island);
	}
};
